/**
 * Preview UI store — owns ephemeral interaction state for the
 * enrollment preview surface (spec §6, §7).
 *
 * Server state (the preview session itself, candidate buckets, validation
 * issues, overrides) lives in TanStack Query — the source of truth is the
 * Redis-backed preview session keyed by `preview_session_id`. This store
 * only tracks per-tab interactions that don't belong in URL state and
 * shouldn't trigger a refetch:
 *
 *   - which recipient row is currently selected (drives the right-pane
 *     per-candidate detail view).
 *   - which step card is expanded inside the right pane (the first card
 *     starts expanded by default; the user can collapse it).
 *
 * The active bucket tab is held in URL search params (`?bucket=...`) per
 * the plan's Task 49 contract so the surface is shareable + survives a
 * refresh — kept out of this store on purpose.
 */

import { create } from "zustand";

/**
 * The three ids the preview surface needs to fetch + mutate a session.
 * When the preview is presented as a drawer (the primary surface) these
 * come from the store instead of route params — the opener (Configure
 * modal, sequence page, etc.) calls `openDrawer(context)` with them.
 */
export interface PreviewDrawerContext {
  jobId: string;
  sequenceId: string;
  previewSessionId: string;
}

export interface PreviewUIState {
  /** Whether the enrollment-preview drawer is mounted/open. */
  open: boolean;
  /** Ids the drawer renders against; `null` while the drawer is closed. */
  context: PreviewDrawerContext | null;
  /** `job_candidate_id` of the row that drives the right-pane preview. */
  selectedJobCandidateId: string | null;
  /** Set of `step_id`s the user has expanded; first step opens by default
   * via `expandedByDefault`, but explicit toggles override that prop. */
  expandedStepIds: Record<string, boolean>;
  /** Open the drawer against a fresh preview session (clears prior
   * ephemeral selection so a new cohort doesn't inherit the last one). */
  openDrawer: (context: PreviewDrawerContext) => void;
  /** Close the drawer and clear context + ephemeral selection. */
  closeDrawer: () => void;
  selectCandidate: (id: string | null) => void;
  toggleStepExpanded: (stepId: string) => void;
  setStepExpanded: (stepId: string, expanded: boolean) => void;
  reset: () => void;
}

/** Ephemeral per-session fields reset on open/close/reset. */
const EPHEMERAL_STATE = {
  selectedJobCandidateId: null as string | null,
  expandedStepIds: {} as Record<string, boolean>,
};

const INITIAL_STATE = {
  open: false,
  context: null as PreviewDrawerContext | null,
  ...EPHEMERAL_STATE,
};

export const usePreviewStore = create<PreviewUIState>((set) => ({
  ...INITIAL_STATE,
  openDrawer: (context) =>
    set({ ...EPHEMERAL_STATE, open: true, context }),
  closeDrawer: () =>
    set({ ...EPHEMERAL_STATE, open: false, context: null }),
  selectCandidate: (id) =>
    set({ selectedJobCandidateId: id }),
  toggleStepExpanded: (stepId) =>
    set((s) => ({
      expandedStepIds: {
        ...s.expandedStepIds,
        [stepId]: !s.expandedStepIds[stepId],
      },
    })),
  setStepExpanded: (stepId, expanded) =>
    set((s) => ({
      expandedStepIds: { ...s.expandedStepIds, [stepId]: expanded },
    })),
  reset: () => set(EPHEMERAL_STATE),
}));
